/**
 * The four billing products, for the grant/usage surfaces only.
 *
 * Deliberately **separate from `@/config/products`** (ENG-1141). That registry
 * is navigation: its `ProductId` is bound to a `ProductRoute` and `_app.tsx`
 * iterates it calling `preloadRoute`. `khorium_agent` has no route, so adding it
 * there would both break the type and send the shell preloading a route that
 * does not exist. The two registries answer different questions — "what can the
 * user navigate to" vs "what can the org hold a grant for" — and billing reads
 * the second.
 *
 * Labels are literals rather than i18n keys, matching `@/config/products`:
 * product names are brand identity and stay untranslated in every locale (see
 * `i18n/locales/en/core.ts`). Only the copy around them is translated.
 *
 * Unit formatting is **not** here. The unit arrives per row on the wire
 * (`GrantResponse.unit` / `ProductUsageResponse.unit`), and hardcoding a
 * product-to-unit map is precisely what the contract tells the FE not to do —
 * see `@/utils/usageFormat`.
 */

/** The four keys the backend catalog registers (`backend/src/job/billing/catalog.py`). */
export type BillingProductKey =
  | 'diagnosis'
  | 'meshgen'
  | 'simops'
  | 'khorium_agent';

export interface BillingProductDef {
  /** The wire key — what `product` carries on both billing endpoints. */
  key: BillingProductKey;
  /** User-visible name. Untranslated brand identity. */
  label: string;
  /**
   * False for `khorium_agent`: it is the bundled AI allowance, what buying the
   * others includes, not a line anyone can purchase. Carried as data so the
   * fuller billing UX (ENG-1146) inherits the semantics instead of re-deriving
   * it from the key.
   */
  purchasable: boolean;
}

/**
 * Display order for the usage surface: the three compute products in pipeline
 * order — diagnose, mesh, simulate, which is the order a part moves through
 * them, not the nav's — then the bundled agent allowance. The agent reads as a
 * consequence of the others, so it belongs last rather than sorted among them.
 *
 * The three compute keys and labels are deliberately duplicated from
 * `@/config/products` rather than imported: this registry must keep answering
 * for `khorium_agent`, which has no entry there. `usageProducts.test.ts` pins
 * the overlap equal, so a rename in either place fails a test instead of
 * quietly showing two names for one product.
 */
export const BILLING_PRODUCTS = [
  { key: 'diagnosis', label: 'Diagnosis', purchasable: true },
  { key: 'meshgen', label: 'MeshGen', purchasable: true },
  { key: 'simops', label: 'SimOps', purchasable: true },
  { key: 'khorium_agent', label: 'Khorium Agent', purchasable: false },
] as const satisfies readonly BillingProductDef[];

/**
 * What can be bought: the three compute products, in the same order. The
 * purchase page's rows, the held-plan set and the scheduled-change note all
 * walk this list, so a product that stops being sellable drops out of every
 * one of them together.
 */
export const SELLABLE_PRODUCTS = BILLING_PRODUCTS.filter(
  (product) => product.purchasable,
);

/**
 * Look up a billing product by wire key.
 *
 * Returns `undefined` rather than throwing: `product` is typed `string` on both
 * endpoints, so a key this build has never heard of is deployment skew (backend
 * ahead of frontend), not a programming error. Callers render such a row by its
 * raw key instead of dropping it — a product the org holds must not vanish from
 * the display because its label is missing.
 */
export function billingProduct(key: string): BillingProductDef | undefined {
  return BILLING_PRODUCTS.find((p) => p.key === key);
}

/** A product's name for a sentence — the raw key for one this build lacks. */
export function billingProductLabel(key: string): string {
  return billingProduct(key)?.label ?? key;
}

/**
 * Product keys in display order: the registry's, then any this build does
 * not recognise, sorted, so a product the org holds under a key this build
 * has no name for still gets its row rather than vanishing. Duplicates fold.
 */
export function inProductOrder(keys: Iterable<string>): string[] {
  const held = new Set(keys);
  const known = BILLING_PRODUCTS.map((p) => p.key).filter((key) =>
    held.has(key),
  );
  const unknown = [...held].filter((key) => !billingProduct(key)).sort();
  return [...known, ...unknown];
}
